// Configure your API base URL here - adjust the subdomain based on your environment
// In development, use the proxy path '/api' which will be forwarded to the remote server
// In production, use the environment variable
const API_BASE_URL = import.meta.env.DEV
    ? '/api'
    : import.meta.env.VITE_REACT_APP_API_URL || '/api';

export interface StatsResponse {
    status: 'success' | 'error';
    data?: {
        singlePlayerGames: number;
        multiplayerGames: number;
        totalPlayers: number;
        onlinePlayers: number;
        activePaidSubscriptions: number;
        activeFreeTrials: number;
        playerTrialInvites: number;
    };
    message?: string;
}

export interface NewsfeedItem {
    id: string;
    title: string;
    description?: string;
    htmlUrl: string;
    mediaUrl?: string;
    mediaType?: string;
    isActive: boolean;
    created: string;
    updated?: string;
}

export interface NewsfeedResponse {
    status: 'success' | 'error';
    data?: NewsfeedItem[];
    message?: string;
}

export interface CreateNewsfeedItemRequest {
    title: string;
    description?: string;
    mediaUrl?: string;
    mediaType?: string;
    isActive?: boolean;
}

export interface UpdateNewsfeedItemRequest {
    title?: string;
    description?: string;
    mediaUrl?: string;
    mediaType?: string;
    isActive?: boolean;
}

export interface NewsfeedItemResponse {
    status: 'success' | 'error';
    data?: NewsfeedItem;
    message?: string;
}

export interface FeedbackItem {
    id: string;
    playerId: string;
    text: string;
    created: string;
}

export interface FeedbackResponse {
    status: 'success' | 'error';
    data?: {
        feedback: FeedbackItem[];
        count: number;
    };
    message?: string;
}

export interface UploadMediaRequest {
    newsfeedItemId: string;
    filename: string;
    contentType: string;
}

export interface UploadMediaResponse {
    status: 'success' | 'error';
    data?: {
        mediaUrl: string;
        signedUrl: string;
        mediaType: string;
        s3Key: string;
    };
    message?: string;
}

export interface Puzzle {
    id: string;
    title: string;
    description?: string;
    photographer: string;
    license: string;
    source: string;
    sourceId?: string;
    tags: {
        country?: string;
        region?: string;
        content?: string[];
    };
    width: number;
    height: number;
    visible: boolean;
    created?: string;
    updated?: string;
}

export interface PuzzlesResponse {
    status: 'success' | 'error';
    data?: {
        items: Puzzle[];
        next?: string;
    };
    message?: string;
    statusCode?: number;
}

export interface PuzzleResponse {
    status: 'success' | 'error';
    data?: Puzzle;
    message?: string;
}

export interface UpdatePuzzleRequest {
    title?: string;
    description?: string;
    photographer?: string;
    license?: string;
    source?: string;
    sourceId?: string;
    tags?: {
        country?: string;
        region?: string;
        content?: string[];
    };
    visible?: boolean;
}

export const adminApi = {
    getStats: async (): Promise<StatsResponse> => {
        try {
            const response = await fetch(`${API_BASE_URL}/admin/stats`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
            });

            const data = await response.json();

            if (!response.ok) {
                return {
                    status: 'error',
                    message: data.message || 'Failed to get stats',
                };
            }

            return {
                status: 'success',
                data: data.data,
            };
        } catch (error) {
            console.error('Get stats error:', error);
            return {
                status: 'error',
                message: 'Network error. Please try again.',
            };
        }
    },

    getAllNewsfeedItems: async (): Promise<NewsfeedResponse> => {
        try {
            const response = await fetch(`${API_BASE_URL}/admin/newsfeed`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
            });

            const data = await response.json();

            if (!response.ok) {
                return {
                    status: 'error',
                    message: data.message || 'Failed to get newsfeed items',
                };
            }

            return {
                status: 'success',
                data: data.data,
            };
        } catch (error) {
            console.error('Get newsfeed items error:', error);
            return {
                status: 'error',
                message: 'Network error. Please try again.',
            };
        }
    },

    createNewsfeedItem: async (
        request: CreateNewsfeedItemRequest
    ): Promise<NewsfeedItemResponse> => {
        try {
            const response = await fetch(`${API_BASE_URL}/admin/newsfeed`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify(request),
            });

            const data = await response.json();

            if (!response.ok) {
                return {
                    status: 'error',
                    message: data.message || 'Failed to create newsfeed item',
                };
            }

            return {
                status: 'success',
                data: data.data,
            };
        } catch (error) {
            console.error('Create newsfeed item error:', error);
            return {
                status: 'error',
                message: 'Network error. Please try again.',
            };
        }
    },

    updateNewsfeedItem: async (
        id: string,
        request: UpdateNewsfeedItemRequest
    ): Promise<NewsfeedItemResponse> => {
        try {
            const response = await fetch(`${API_BASE_URL}/admin/newsfeed/${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify(request),
            });

            const data = await response.json();

            if (!response.ok) {
                return {
                    status: 'error',
                    message: data.message || 'Failed to update newsfeed item',
                };
            }

            return {
                status: 'success',
                data: data.data,
            };
        } catch (error) {
            console.error('Update newsfeed item error:', error);
            return {
                status: 'error',
                message: 'Network error. Please try again.',
            };
        }
    },

    deleteNewsfeedItem: async (
        id: string
    ): Promise<{ status: 'success' | 'error'; message?: string }> => {
        try {
            const response = await fetch(`${API_BASE_URL}/admin/newsfeed/${id}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
            });

            const data = await response.json();

            if (!response.ok) {
                return {
                    status: 'error',
                    message: data.message || 'Failed to delete newsfeed item',
                };
            }

            return {
                status: 'success',
                message: data.message || 'Newsfeed item deleted successfully',
            };
        } catch (error) {
            console.error('Delete newsfeed item error:', error);
            return {
                status: 'error',
                message: 'Network error. Please try again.',
            };
        }
    },

    resetUserNewsfeedTracking: async (
        playerId: string
    ): Promise<{ status: 'success' | 'error'; message?: string }> => {
        try {
            const response = await fetch(`${API_BASE_URL}/admin/newsfeed/reset/${playerId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
            });

            const data = await response.json();

            if (!response.ok) {
                return {
                    status: 'error',
                    message: data.message || 'Failed to reset user newsfeed tracking',
                };
            }

            return {
                status: 'success',
                message: data.message || 'User newsfeed tracking reset successfully',
            };
        } catch (error) {
            console.error('Reset user newsfeed tracking error:', error);
            return {
                status: 'error',
                message: 'Network error. Please try again.',
            };
        }
    },

    getAllFeedback: async (limit?: number): Promise<FeedbackResponse> => {
        try {
            let urlStr = `${API_BASE_URL}/admin/feedback`;

            if (limit) {
                urlStr += `?limit=${limit}`;
            }

            const response = await fetch(urlStr, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
            });

            const data = await response.json();

            if (!response.ok) {
                return {
                    status: 'error',
                    message: data.message || 'Failed to get feedback',
                };
            }

            return {
                status: 'success',
                data: data.data,
            };
        } catch (error) {
            console.error('Get feedback error:', error);
            return {
                status: 'error',
                message: 'Network error. Please try again.',
            };
        }
    },

    uploadNewsfeedMedia: async (request: UploadMediaRequest): Promise<UploadMediaResponse> => {
        try {
            const response = await fetch(`${API_BASE_URL}/admin/newsfeed/upload-media`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify(request),
            });

            const data = await response.json();

            if (!response.ok) {
                return {
                    status: 'error',
                    message: data.message || 'Failed to get upload URL',
                };
            }

            return {
                status: 'success',
                data: data.data,
            };
        } catch (error) {
            console.error('Upload media error:', error);
            return {
                status: 'error',
                message: 'Network error. Please try again.',
            };
        }
    },

    getPuzzles: async (limit?: number, next?: string): Promise<PuzzlesResponse> => {
        try {
            let urlStr = `${API_BASE_URL}/puzzles`;
            const params = new URLSearchParams();
            if (limit) {
                params.append('limit', limit.toString());
            }
            if (next) {
                params.append('next', next);
            }
            if (params.toString()) {
                urlStr += `?${params.toString()}`;
            }

            const response = await fetch(urlStr, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
            });

            const data = await response.json();

            if (!response.ok) {
                return {
                    status: 'error',
                    message: data.message || data.error || 'Failed to get puzzles',
                    statusCode: response.status,
                };
            }

            // The API returns a Paged<Puzzle> which is an array with a 'next' property
            // When JSON.stringify'd, it becomes an array with a 'next' property
            const puzzles = Array.isArray(data) ? data : [];
            const nextToken = data.next;

            return {
                status: 'success',
                data: {
                    items: puzzles,
                    next: nextToken,
                },
            };
        } catch (error) {
            console.error('Get puzzles error:', error);
            return {
                status: 'error',
                message: 'Network error. Please try again.',
            };
        }
    },

    updatePuzzle: async (id: string, request: UpdatePuzzleRequest): Promise<PuzzleResponse> => {
        try {
            const response = await fetch(`${API_BASE_URL}/puzzle/${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify(request),
            });

            const data = await response.json();

            if (!response.ok) {
                return {
                    status: 'error',
                    message: data.message || data.error || 'Failed to update puzzle',
                };
            }

            return {
                status: 'success',
                data: data,
            };
        } catch (error) {
            console.error('Update puzzle error:', error);
            return {
                status: 'error',
                message: 'Network error. Please try again.',
            };
        }
    },

    publishCatalog: async (): Promise<{ status: 'success' | 'error'; message?: string }> => {
        try {
            const response = await fetch(`${API_BASE_URL}/publish/catalog`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
            });

            const data = await response.json();

            if (!response.ok) {
                return {
                    status: 'error',
                    message: data.message || data.error || 'Failed to publish catalog',
                };
            }

            return {
                status: 'success',
                message: data.message || 'Catalog published successfully',
            };
        } catch (error) {
            console.error('Publish catalog error:', error);
            return {
                status: 'error',
                message: 'Network error. Please try again.',
            };
        }
    },
};
